import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeader } from '../context/AuthContext';
import { getAuthToken } from '../utils/authStorage';
import { useLanguage } from '../context/LanguageContext';
import { Bell, X, AlertCircle, Clock, FileText, Briefcase, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NotificationsPanel = () => {
  const { t, isRTL } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    // Refresh every 2 minutes
    const interval = setInterval(fetchNotifications, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    // Skip when not authenticated to avoid 403 polling noise
    if (!getAuthToken()) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API}/notifications`, {
        headers: getAuthHeader()
      });
      // Backend returns either an array (legacy) or {notifications, unread}
      const payload = response.data;
      if (Array.isArray(payload)) {
        setNotifications(payload);
        setUnreadCount(payload.filter((n) => !n.is_read).length);
      } else {
        setNotifications(payload.notifications || []);
        setUnreadCount(payload.unread || 0);
      }
    } catch (error) {
      // Silently ignore 401/403 (e.g., role without notifications access) to avoid console noise
      const status = error?.response?.status;
      if (status !== 401 && status !== 403) {
        console.error('Failed to fetch notifications:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/notifications/${notificationId}/read`, {}, {
        headers: getAuthHeader()
      });
      fetchNotifications();
    } catch (error) {
      const status = error?.response?.status;
      if (status !== 401 && status !== 403) {
        console.error('Failed to mark notification as read:', error);
      }
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.patch(`${API}/notifications/read-all`, {}, {
        headers: getAuthHeader()
      });
      fetchNotifications();
    } catch (error) {
      const status = error?.response?.status;
      if (status !== 401 && status !== 403) {
        console.error('Failed to mark all notifications as read:', error);
      }
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'invoice_overdue':
      case 'invoice_due_soon':
        return <FileText className="text-orange-500" size={20} />;
      case 'project_overdue':
      case 'project_due_soon':
        return <Briefcase className="text-blue-500" size={20} />;
      case 'project_completed':
        return <CheckCircle className="text-green-500" size={20} />;
      default:
        return <AlertCircle className="text-slate-500" size={20} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-r-4 border-red-500 bg-red-50';
      case 'medium':
        return 'border-r-4 border-orange-500 bg-orange-50';
      case 'low':
        return 'border-r-4 border-blue-500 bg-blue-50';
      default:
        return 'border-r-4 border-slate-500 bg-slate-50';
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark stored notifications as read
    if (notification.stored) {
      markAsRead(notification.id);
    }
    
    if (notification.type.includes('invoice')) {
      navigate('/invoices');
    } else if (notification.type.includes('project')) {
      navigate('/projects');
    }
    setIsOpen(false);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('now');
    if (diffMins < 60) return t('minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('hoursAgo', { count: diffHours });
    return t('daysAgo', { count: diffDays });
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-blue-700 transition-colors"
        data-testid="notifications-bell"
      >
        <Bell className="text-white" size={22} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-30"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Panel */}
          <div className="absolute left-0 sm:-left-40 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-2xl border z-40 max-h-[500px] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b bg-primary text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell size={20} />
                <h3 className="font-cairo font-semibold">الإشعارات</h3>
                {unreadCount > 0 && (
                  <span className="bg-accent text-slate-900 text-xs px-2 py-0.5 rounded-full font-bold">
                    {unreadCount}
                  </span>
                )}
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-blue-700 p-1 rounded"
              >
                <X size={18} />
              </button>
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="text-sm text-slate-600 font-cairo mt-2">جاري التحميل...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="mx-auto h-12 w-12 text-slate-300 mb-2" />
                  <p className="text-slate-600 font-cairo">لا توجد إشعارات جديدة</p>
                  <p className="text-sm text-slate-500 font-cairo mt-1">ستظهر الإشعارات هنا عند وجود تحديثات</p>
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${getPriorityColor(notification.priority)}`}
                      data-testid="notification-item"
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-1">
                            <h4 className="font-cairo font-semibold text-slate-900 text-sm">
                              {notification.title}
                            </h4>
                            {notification.priority === 'high' && (
                              <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded font-cairo">
                                عاجل
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-700 font-cairo mb-2">
                            {notification.message}
                          </p>
                          
                          {/* Additional Data */}
                          {notification.data && (
                            <div className="text-xs text-slate-600 font-cairo space-y-1">
                              {notification.data.invoice_number && (
                                <p>• رقم الفاتورة: {notification.data.invoice_number}</p>
                              )}
                              {notification.data.project_name && (
                                <p>• المشروع: {notification.data.project_name}</p>
                              )}
                              {notification.data.designer && (
                                <p>• المصمم: {notification.data.designer}</p>
                              )}
                              {notification.data.amount && (
                                <p>• المبلغ: {notification.data.amount.toLocaleString()} {notification.data.currency}</p>
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                            <Clock size={12} />
                            <span className="font-cairo">{formatTimeAgo(notification.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-3 border-t bg-slate-50 flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 font-cairo text-sm"
                  onClick={() => {
                    fetchNotifications();
                  }}
                >
                  تحديث
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 font-cairo text-sm text-primary"
                  onClick={markAllAsRead}
                >
                  تحديد الكل كمقروء
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
